import SEARCH_CLASSROOMS_QUERY from 'graph/classroom/search-classrooms-query';
import SEARCH_ORGANIZATIONS_QUERY from 'graph/organization/search-organizations-query';
import SEARCH_STUDENTS_QUERY from 'graph/student/search-students-query';
import SEARCH_USERS_QUERY from 'graph/user/search-users-query';
import GET_BASE_ORGANIZATION_QUERY from 'graph/organization/get-base-organization-query';
import GET_BASE_USER_QUERY from 'graph/user/get-base-user-query';
import GET_BASE_CLASSROOM_QUERY from 'graph/classroom/get-base-classroom-query';
import GET_BASE_STUDENT_QUERY from 'graph/student/get-base-student-query';
import { keyBy } from 'lodash';
import { PrivilegesCache } from 'store/user';
import { OrganizationType, User } from 'types/schema-generated-types';
import { resolveDateRange } from './report-date-utils';
import {
  ReportParams,
  ReportEntityType,
  ReportDateRange,
  ReportProduct,
  ReportDisplayBy
} from '../types';

export const ENTITY_SEARCH_LIMIT = 50;

const parseOrganizationSearchData = (data: any): any => data.organizations;

const getOrganizationOptionsLabel = (entity: any): string => entity.name;

const ENTITY_VARIABLE_MAP = {
  [ReportEntityType.DISTRICT]: 'organizationId',
  [ReportEntityType.SCHOOL]: 'organizationId',
  [ReportEntityType.USER_GROUP]: 'organizationId',
  [ReportEntityType.USER]: 'userId',
  [ReportEntityType.CLASSROOM]: 'classroomId',
  [ReportEntityType.STUDENT]: 'studentId'
};

export const PRODUCT_FAMILY_OPTIONS = [
  {
    value: ReportProduct.KEY,
    requiredPrivileges: ['keyboarding-report.read.org', 'keyboarding-report.read.classroom']
  },
  {
    value: ReportProduct.WDT,
    requiredPrivileges: ['wdt-report.read.org', 'wdt-report.read.classroom']
  },
  {
    value: ReportProduct.PRM,
    requiredPrivileges: ['prm-report.read.org', 'prm-report.read.classroom']
  }
];

export const PRODUCT_FAMILY_OPTIONS_MAP = keyBy(PRODUCT_FAMILY_OPTIONS, 'value');

export const ENTITY_TYPE_OPTIONS = [
  {
    type: ReportEntityType.DISTRICT,
    organizationType: OrganizationType.DISTRICT,
    requiredOrgPrivilege: {
      [ReportProduct.KEY]: 'keyboarding-report.read.org',
      [ReportProduct.PRM]: 'prm-report.read.org'
    },
    getEntityQuery: GET_BASE_ORGANIZATION_QUERY,
    query: SEARCH_ORGANIZATIONS_QUERY,
    queryVariables: { organizationType: OrganizationType.DISTRICT },
    parseQueryData: parseOrganizationSearchData,
    getOptionLabel: getOrganizationOptionsLabel
  },
  {
    type: ReportEntityType.SCHOOL,
    organizationType: OrganizationType.SCHOOL,
    requiredOrgPrivilege: {
      [ReportProduct.KEY]: 'keyboarding-report.read.org',
      [ReportProduct.PRM]: 'prm-report.read.org'
    },
    getEntityQuery: GET_BASE_ORGANIZATION_QUERY,
    query: SEARCH_ORGANIZATIONS_QUERY,
    queryVariables: { organizationType: OrganizationType.SCHOOL },
    parseQueryData: parseOrganizationSearchData,
    getOptionLabel: getOrganizationOptionsLabel
  },
  {
    type: ReportEntityType.USER_GROUP,
    getEntityQuery: GET_BASE_ORGANIZATION_QUERY,
    organizationType: OrganizationType.USER_GROUP,
    requiredOrgPrivilege: {
      [ReportProduct.KEY]: 'keyboarding-report.read.org',
      [ReportProduct.PRM]: 'prm-report.read.org'
    },
    query: SEARCH_ORGANIZATIONS_QUERY,
    queryVariables: { organizationType: OrganizationType.USER_GROUP },
    parseQueryData: parseOrganizationSearchData,
    getOptionLabel: getOrganizationOptionsLabel
  },
  {
    type: ReportEntityType.USER,
    requiredClassroomPrivilege: {
      [ReportProduct.KEY]: 'keyboarding-report.read.classroom',
      [ReportProduct.PRM]: 'prm-report.read.classroom'
    },
    requiredOrgPrivilege: {
      [ReportProduct.KEY]: 'keyboarding-report.read.org',
      [ReportProduct.PRM]: 'prm-report.read.org'
    },
    getEntityQuery: GET_BASE_USER_QUERY,
    query: SEARCH_USERS_QUERY,
    parseQueryData: (data: any, currentUser: User, userPrivilegesCache: PrivilegesCache): any[] => {
      const users = data.userSearch;

      // If the user doesn't have the reporting privilege at an organization level. then really the only
      // user they should be able to view are themselves.
      if (userPrivilegesCache.getPrivilegedOrgIds('keyboarding-report.read.org').length === 0) {
        return users.filter((user: User) => user.id === currentUser.id);
      }

      return users;
    },
    getOptionLabel: (entity: any): string =>
      `${entity.firstName} ${entity.lastName} (${entity.username})`
  },
  {
    type: ReportEntityType.CLASSROOM,
    requiredClassroomPrivilege: {
      [ReportProduct.KEY]: 'keyboarding-report.read.classroom',
      [ReportProduct.WDT]: 'wdt-report.read.classroom',
      [ReportProduct.PRM]: 'prm-report.read.classroom'
    },
    requiredOrgPrivilege: {
      [ReportProduct.KEY]: 'keyboarding-report.read.org',
      [ReportProduct.WDT]: 'wdt-report.read.org',
      [ReportProduct.PRM]: 'prm-report.read.org'
    },
    getEntityQuery: GET_BASE_CLASSROOM_QUERY,
    query: SEARCH_CLASSROOMS_QUERY,
    parseQueryData: (data: any): any[] => data.classrooms,
    getOptionLabel: (entity: any): string => entity.name
  },
  {
    type: ReportEntityType.STUDENT,
    requiredClassroomPrivilege: {
      [ReportProduct.KEY]: 'keyboarding-report.read.classroom',
      [ReportProduct.WDT]: 'wdt-report.read.classroom',
      [ReportProduct.PRM]: 'prm-report.read.classroom'
    },
    requiredOrgPrivilege: {
      [ReportProduct.KEY]: 'keyboarding-report.read.org',
      [ReportProduct.WDT]: 'wdt-report.read.org',
      [ReportProduct.PRM]: 'prm-report.read.org'
    },
    getEntityQuery: GET_BASE_STUDENT_QUERY,
    query: SEARCH_STUDENTS_QUERY,
    parseQueryData: (data: any): any[] => data.studentSearch,
    getOptionLabel: (entity: any): string => {
      let label = `${entity.firstName} ${entity.lastName}`;
      if (entity.username) label += ` (${entity.username})`;
      return label;
    }
  }
];

export const ENTITY_TYPE_OPTIONS_MAP = keyBy(ENTITY_TYPE_OPTIONS, 'type');

export const DISPLAY_BY_OPTIONS = [
  { name: 'Characters', value: ReportDisplayBy.CHARACTERS },
  { name: 'Students', value: ReportDisplayBy.STUDENTS }
];

export const DATE_RANGE_OPTIONS = [
  { name: '7 Days', value: ReportDateRange.SEVEN_DAYS },
  { name: '14 Days', value: ReportDateRange.FOURTEEN_DAYS },
  { name: '30 Days', value: ReportDateRange.THRITY_DAYS },
  { name: '3 Months', value: ReportDateRange.THREE_MONTHS },
  { name: '6 Months', value: ReportDateRange.SIX_MONTHS },
  { name: '9 Months', value: ReportDateRange.NINE_MONTHS },
  { name: '1 Year', value: ReportDateRange.ONE_YEAR }
];

export const getEntityType = (
  organizationType?: OrganizationType
): ReportEntityType | undefined => {
  if (!organizationType) return undefined;

  const entityTypeOption = ENTITY_TYPE_OPTIONS.find(
    option => option.organizationType === organizationType
  );

  return entityTypeOption?.type;
};

export const ORGANIZATION_ENTITY_MAP = {
  [OrganizationType.DISTRICT]: ReportEntityType.DISTRICT,
  [OrganizationType.SCHOOL]: ReportEntityType.SCHOOL,
  [OrganizationType.USER_GROUP]: ReportEntityType.USER_GROUP
};

export const isOrganizationEntityType = (entityType: ReportEntityType): boolean =>
  [ReportEntityType.DISTRICT, ReportEntityType.SCHOOL, ReportEntityType.USER_GROUP].includes(
    entityType
  );

export const getQueryVariables = ({
  entityId,
  entityType,
  dateRange
}: Partial<ReportParams>): Record<string, any> => {
  return {
    where: {
      ...(entityType && entityId && { [ENTITY_VARIABLE_MAP[entityType]]: entityId })
    },
    ...(dateRange && { dateRange: resolveDateRange(dateRange) })
  };
};
